import { utc, UTC, DateRange, DateType, isNullOrUndefined } from 'rewire-common';
import { observable, freeze } from 'rewire-core';
import { Form, IFormContext, IFieldDefn } from 'rewire-ui';

export type DateDuration =
  | '1 Year'
  | '1 Month'
  | '4 Weeks'
  | '3 Weeks'
  | '2 Weeks'
  | '1 Week'
  | '1 Day'
  | '1st Quarter'
  | '2nd Quarter'
  | '3rd Quarter'
  | '4th Quarter'
  | 'Custom';

const daysToDuration = (item: number, effective: DateRange): DateDuration => {
  if (effective.start.month === 1 && effective.end.month === 3) return '1st Quarter';
  if (effective.start.month === 4 && effective.end.month === 6) return '2nd Quarter';
  if (effective.start.month === 7 && effective.end.month === 9) return '3rd Quarter';
  if (effective.start.month === 10 && effective.end.month === 12) return '4th Quarter';

  switch (item) {
    case 1:
      return '1 Day';
    case 7:
      return '1 Week';
    case 14:
      return '2 Weeks';
    case 21:
      return '3 Weeks';
    case 28:
      return '4 Weeks';
    case 29:
    case 30:
    case 31:
      return '1 Month';
    case 365:
      return '1 Year';
    case 366:
      return '1 Year';
    default:
      return '2 Weeks';
  }
};

const durationToDaysBase = {
  '1 Day': 1,
  '1 Week': 7,
  '2 Weeks': 14,
  '3 Weeks': 21,
  '4 Weeks': 28,
};

const durationToDaysMonth = {
  '1 Month': 31,
};

const durationToDaysQuarters = {
  '1st Quarter': 93,
  '2nd Quarter': 93,
  '3rd Quarter': 93,
  '4th Quarter': 93,
};

const durationToDaysCustom = {
  Custom: 366,
};

const durationToDaysYear = {
  '1 Year': 366,
};

export const durationToDays = {
  ...durationToDaysBase,
  ...durationToDaysMonth,
  ...durationToDaysYear,
  ...durationToDaysQuarters,
  ...durationToDaysCustom,
};

function makeEffective(date: DateType, duration: DateDuration): DateRange {
  let start = utc(date);
  let end = utc(date);
  const year = start.year;

  switch (duration) {
    case '1 Day':
      end = utc(start);
      break;
    case '1 Week':
      end = start.add(6);
      break;
    case '2 Weeks':
      end = start.add(13);
      break;
    case '3 Weeks':
      end = start.add(20);
      break;
    case '4 Weeks':
      end = start.add(27);
      break;

    case '1 Month':
      start = UTC.ymd(start.year, start.month, 1);
      end = UTC.ymd(start.year, start.month + 1, 1).add(-1);
      break;

    case '1 Year':
      end = UTC.ymd(year + 1, start.month, start.day).add(-1);
      break;

    case '1st Quarter':
      start = UTC.ymd(year, 1, 1);
      end = UTC.ymd(year, 3, 31);
      break;

    case '2nd Quarter':
      start = UTC.ymd(year, 4, 1);
      end = UTC.ymd(year, 6, 30);
      break;

    case '3rd Quarter':
      start = UTC.ymd(year, 7, 1);
      end = UTC.ymd(year, 9, 30);
      break;

    case '4th Quarter':
      start = UTC.ymd(year, 10, 1);
      end = UTC.ymd(year, 12, 31);
      break;
  }
  return new DateRange(start, end);
}

type DateRangeNavOnChangeFn = (value: any, effective: DateRange) => void;

interface IDateRangeNavModelState {
  showEndDate: boolean;
  dateError?: string;
  effective?: DateRange;
  maxDays: number;
  allowMonth: boolean;
  allowQuarters: boolean;
  disabled: boolean;
  disableDate: boolean;
  disableDuration: boolean;
}

export default class DateRangeNavModel {
  form: Form;
  state: IDateRangeNavModelState;

  static DEFAULT_MAX_DAYS = 14;

  constructor(
    protected _onStartDateChange?: DateRangeNavOnChangeFn,
    protected _onEndDateChange?: DateRangeNavOnChangeFn,
    protected _onDateDurationChange?: DateRangeNavOnChangeFn,
    customFormDatePicker?: (form: IFormContext, editProps?: any) => IFieldDefn,
  ) {
    this.form = Form.create(
      (form: IFormContext) => ({
        startDate: (!isNullOrUndefined(customFormDatePicker) ? customFormDatePicker(form) : form.date())
          .updateOnChange()
          .onValueChange(this.handleStartDateChange)
          .disabled(() => this.disabled || this.disableDate || this.usingQuarters),
        endDate: (!isNullOrUndefined(customFormDatePicker) ? customFormDatePicker(form) : form.date())
          .updateOnChange()
          .onValueChange(this.handleEndDateChange)
          .disabled(() => this.disabled || this.disableDate),
        dateDuration: form
          .select(this.getDateDurationSearcher(), { dynamic: true })
          .onValueChange(this.handleDateDurationChange)
          .disabled(() => this.disabled || this.disableDuration),
      }),
      {},
      { disableErrors: true },
    );

    this.state = observable({
      showEndDate: false,
      effective: undefined,
      maxDays: DateRangeNavModel.DEFAULT_MAX_DAYS,
      allowMonth: false,
      allowQuarters: false,
      disabled: false,
      disableDate: false,
      disableDuration: false,
    });
  }

  reset() {
    freeze(() => {
      this.state.showEndDate = false;
      this.state.effective = undefined;
      this.state.maxDays = DateRangeNavModel.DEFAULT_MAX_DAYS;
      this.state.allowMonth = false;
      this.state.allowQuarters = false;
      this.state.disabled = false;
      this.state.disableDate = false;
      this.state.disableDuration = false;
    });
  }

  load(data: { effective?: DateRange; maxDays?: number; allowMonth?: boolean; allowQuarters?: boolean }) {
    this.state.maxDays = data.maxDays ?? DateRangeNavModel.DEFAULT_MAX_DAYS;
    this.state.allowMonth = !!data.allowMonth;
    this.state.allowQuarters = !!data.allowQuarters;
    if (data.effective) {
      this.state.effective = data.effective;
      this.form.field.startDate.value = utc(data.effective.start);
      const days = data.effective.length;

      if (!this.allowQuarters || !this.isCustom) {
        this.form.field.dateDuration.value = daysToDuration(days, data.effective);
      }
    }
  }

  get startDateFieldValue(): UTC | undefined {
    return this.form.field.startDate.value;
  }

  get endDateFieldValue(): UTC | undefined {
    return this.form.field.endDate.value;
  }

  set endDateFieldValue(value: UTC | undefined) {
    this.form.field.endDate.value = value;
  }

  get dateDuration(): DateDuration | undefined {
    return this.form.field.dateDuration.value;
  }

  get dateDurationDays(): number | undefined {
    return !isNullOrUndefined(this.dateDuration) ? this.durationToNumberOfDays(this.dateDuration) : undefined;
  }

  durationToNumberOfDays(dateDuration: DateDuration): number {
    return this.isCustom
      ? isNullOrUndefined(this.startDateFieldValue) || isNullOrUndefined(this.endDateFieldValue)
        ? DateRangeNavModel.DEFAULT_MAX_DAYS
        : new DateRange(this.startDateFieldValue, this.endDateFieldValue).length
      : (durationToDays[dateDuration] ?? DateRangeNavModel.DEFAULT_MAX_DAYS);
  }

  get showEndDate(): boolean {
    return this.state.showEndDate;
  }

  set showEndDate(value: boolean) {
    this.state.showEndDate = value;
  }

  get dateError(): string | undefined {
    return this.state.dateError;
  }

  set dateError(value: string | undefined) {
    this.state.dateError = value;
  }

  get hasDateError(): boolean {
    return this.dateError !== undefined;
  }

  get effective(): DateRange | undefined {
    return this.state.effective;
  }

  get startDate(): UTC | undefined {
    return this.effective?.start;
  }

  get endDate(): UTC | undefined {
    return this.effective?.end;
  }

  get startDateStartOfDay(): UTC | undefined {
    return this.startDate?.clone().startOfDay();
  }

  get endDateStartOfDay(): UTC | undefined {
    return this.endDate?.clone().startOfDay();
  }

  get numOfDays(): number | undefined {
    return this.effective?.length;
  }

  get maxDays(): number {
    return this.state.maxDays;
  }

  get allowMonth(): boolean {
    return this.state.allowMonth;
  }

  get allowQuarters(): boolean {
    return this.state.allowQuarters;
  }

  setDateToPrevious() {
    if (this.disableNavigation) return;
    const dateDurationDays = this.dateDurationDays!;
    const startDate = this.startDateFieldValue!;
    this.form.setFieldValue('startDate', startDate!.add(-dateDurationDays));
    if (this.isCustom) {
      this.form.setFieldValue('endDate', startDate!.add(dateDurationDays - 1));
    }
  }

  setDateToNext() {
    if (this.disableNavigation) return;
    const dateDurationDays = this.dateDurationDays!;
    const startDate = this.startDateFieldValue!;
    this.form.setFieldValue('startDate', startDate.add(dateDurationDays));
    if (this.isCustom) {
      this.form.setFieldValue('endDate', startDate.add(dateDurationDays - 1));
    }
  }

  get isCustom(): boolean {
    return this.dateDuration === 'Custom';
  }

  set disabled(value: boolean) {
    this.state.disabled = value;
  }

  get disabled(): boolean {
    return this.state.disabled;
  }

  set disableDate(value: boolean) {
    this.state.disableDate = value;
  }

  get disableDate(): boolean {
    return this.state.disableDate;
  }

  set disableDuration(value: boolean) {
    this.state.disableDuration = value;
  }

  get disableDuration(): boolean {
    return this.state.disableDuration;
  }

  get disableNavigation(): boolean {
    return (
      this.disabled || this.disableDate || !this.startDateFieldValue || isNullOrUndefined(this.dateDuration) || this.usingQuarters
    );
  }

  get usingQuarters(): boolean {
    return !isNullOrUndefined(this.dateDuration) && Object.keys(durationToDaysQuarters).includes(this.dateDuration);
  }

  handleStartDateChange = (_form: Form, value: UTC) => {
    this.dateError = undefined;
    if (!value || !this.dateDuration) return;
    const effective = makeEffective(value, this.dateDuration);
    if (effective.isValid) {
      this.state.effective = effective;
      this._onStartDateChange?.(value, effective);
    }
  };

  handleEndDateChange = (_form: Form, value: UTC) => {
    this.dateError = undefined;
    if (!value || !this.startDateFieldValue) return;
    const effective = new DateRange(this.startDateFieldValue, value);
    this.assessCustomEffectiveRange(effective);
    if (effective.isValid) {
      this.state.effective = effective;
      this._onEndDateChange?.(value, effective);
    }
  };

  handleDateDurationChange = (_form: Form, value: DateDuration) => {
    this.dateError = undefined;
    if (!value || !this.startDateFieldValue) return;
    this.showEndDate = this.isCustom;
    let effective: DateRange;
    if (this.isCustom) {
      this.endDateFieldValue = this.startDateFieldValue.add(this.dateDurationDays!);
      effective = new DateRange(this.startDateFieldValue, this.endDateFieldValue);
      this.assessCustomEffectiveRange(effective);
    } else {
      effective = makeEffective(this.startDateFieldValue, value);
    }

    if (effective.isValid) {
      this.state.effective = effective;
      this._onDateDurationChange?.(value, effective);
    }
  };

  assessCustomEffectiveRange(effective: DateRange) {
    if (effective.end.utc === UTC.MaxValue.utc) {
      this.dateError = 'a value must be specified for End Date';
    } else if (effective.end.utc < effective.start.utc) {
      this.dateError = 'End Date must occur on or after Start Date';
    } else if (effective.length > 99) {
      const maxEndDate = effective.start.add(98);
      this.dateError = `number of days cannot exceed 99 (${maxEndDate.toDateString()})`;
    }
  }

  // find the array element with a value closest to and <= the specified maxDays
  getElementForMaxDaysBase(): number {
    const durationValues = Object.values(durationToDaysBase);
    let days = 0;
    let currentElement = 0;
    for (let i = 0; i < durationValues.length; ++i) {
      const dayValue = durationValues[i];
      if (dayValue > days && dayValue <= this.maxDays) {
        days = dayValue;
        currentElement = i;
      }
    }
    return currentElement;
  }

  addMonthIfSupported(suggestions: string[]) {
    if (this.maxDays >= durationToDaysMonth['1 Month'] || this.allowMonth) {
      suggestions.push(...['1 Month']);
    }
  }

  addYearIfSupported(suggestions: string[]) {
    if (this.maxDays >= durationToDaysYear['1 Year']) {
      suggestions.push(...['1 Year']);
    }
  }

  addQuartersIfSupported(suggestions: string[]) {
    if (this.allowQuarters) {
      suggestions.push(...['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter', 'Custom']);
    }
  }

  getDateDurationSearcher() {
    const _map = (item?: any) => item || '';
    const dateDurationSearch = (): string[] => {
      const durationStrings = Object.keys(durationToDaysBase);
      const element = this.getElementForMaxDaysBase();
      const suggestions = durationStrings.slice(0, element + 1);
      this.addMonthIfSupported(suggestions);
      this.addYearIfSupported(suggestions);
      this.addQuartersIfSupported(suggestions);
      return suggestions;
    };
    return { map: _map, search: dateDurationSearch };
  }
}
